// hide-images.component.ts

import { DOCUMENT, NgIf, NgClass, NgFor } from '@angular/common';
import { Component, OnInit, OnDestroy, Renderer2, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AccessibilityService } from './services/accessibility.service';
import {AstralCheckmarkSvgComponent} from "./util/astral-checksvg.component";

@Component({
  selector: 'tgsd-hide-images',
  standalone: true,
  template: `
    <button
      (click)="nextState()"
      [ngClass]="{ 'is-use': currentState !== 0 }"
      [attr.aria-label]="getAriaLabel()"
      [attr.aria-pressed]="currentState !== 0"
      aria-live="polite"
      class="feature-item-button"
    >
<!--      <div class="icon active active-check" *ngIf="currentState !== 0">-->
<!--        <svg-->
<!--          width="17px"-->
<!--          height="13px"-->
<!--          viewBox="0 0 17 13"-->
<!--          version="1.1"-->
<!--          xmlns="http://www.w3.org/2000/svg"-->
<!--          xmlns:xlink="http://www.w3.org/1999/xlink"-->
<!--        >-->
<!--          <g-->
<!--            id="Page-1"-->
<!--            stroke="none"-->
<!--            stroke-width="1"-->
<!--            fill="none"-->
<!--            fill-rule="evenodd"-->
<!--          >-->
<!--            <g-->
<!--              id="Different-States"-->
<!--              transform="translate(-1004.000000, -279.000000)"-->
<!--              fill="#ffffff"-->
<!--            >-->
<!--              <g-->
<!--                id="Group-28-Copy-12"-->
<!--                transform="translate(592.000000, 224.000000)"-->
<!--              >-->
<!--                <g id="Group-56" transform="translate(98.000000, 20.000000)">-->
<!--                  <g-->
<!--                    id="Group-3-Copy-7"-->
<!--                    transform="translate(296.000000, 16.000000)"-->
<!--                  >-->
<!--                    <path-->
<!--                      d="M34.5748969,22.1199038 L25.4067007,31.2161926 C24.8673951,31.7554982 24.004506,31.7914519 23.4292466,31.2521463 L18.5754957,26.8298399 C18.0002363,26.2905342 17.9642826,25.3916914 18.4676346,24.8164321 C19.0069402,24.2411727 19.905783,24.205219 20.4810424,24.7445246 L24.3280894,28.2679883 L32.5255354,20.0705423 C33.1007948,19.4952829 33.9996376,19.4952829 34.5748969,20.0705423 C35.1501563,20.6458016 35.1501563,21.5446444 34.5748969,22.1199038 Z"-->
<!--                      id="Path"-->
<!--                    ></path>-->
<!--                  </g>-->
<!--                </g>-->
<!--              </g>-->
<!--            </g>-->
<!--          </g>-->
<!--        </svg>-->
<!--      </div>-->

      <span class="feature-icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <path
            d="M2 2l20 20M6.713 6.713a8.938 8.938 0 00-4.654 5.176C3.763 15.197 7.585 18 12 18a8.99 8.99 0 005.287-1.713M9.879 9.879a3 3 0 104.242 4.242M12 4.5C16.415 4.5 20.237 7.303 21.94 11.89a8.938 8.938 0 01-2.07 4.397"
          />
        </svg>
      </span>

      <span class="feature-label">{{ getDisplayLabel() }}</span>

      <astral-widget-checkmark
        [isActive]="currentState !== 0"
      ></astral-widget-checkmark>
    </button>
  `,
  imports: [NgIf, NgClass, NgFor, AstralCheckmarkSvgComponent],
  styles: [
    `
      :host {
        display: contents;
      }
      .feature-item-button {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 8px;
        width: 100%;
        height: 100%;
        padding: 12px 8px;
        text-align: center;
        /* Default background and border for single-state components */
        background-color: #f8f9fb;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        color: #333;
        cursor: pointer;
        transition: all 0.2s ease-in-out;
        font-family: inherit;
      }
      .feature-item-button:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        border-color: #007bff;
      }
      .feature-item-button.active {
        color: #007bff;
        border-color: #007bff;
      }
      .active-tick {
        position: absolute;
        top: 5px;
        right: 5px;
        color: #28a745;
      }
      .active-tick svg {
        width: 20px;
        height: 20px;
      }
      .feature-icon {
        margin-bottom: 8px;
        color: #666;
      }
      .feature-item-button.active .feature-icon {
        color: #007bff;
      }
      .feature-icon svg {
        width: 28px;
        height: 28px;
      }
      .feature-label {
        font-size: 13px;
        font-weight: 600;
        line-height: 1.3;
      }
      .state-indicators {
        position: absolute;
        bottom: 8px;
        display: flex;
        gap: 5px;
        justify-content: center;
      }
      .state-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background-color: rgba(0, 0, 0, 0.2);
        transition: all 0.2s ease;
      }
      .state-dot.is-active {
        background-color: #007bff;
        transform: scale(1.1);
      }
    `,
  ],
})
export class HideImagesComponent implements OnInit, OnDestroy {
  private document = inject(DOCUMENT);
  private renderer = inject(Renderer2);
  private accessibilityService = inject(AccessibilityService);
  private destroy$ = new Subject<void>();

  public currentState = 0;
  public readonly states = ['Hide Images', 'Images Hidden'];
  private _styleElement: HTMLStyleElement | null = null;

  ngOnInit(): void {
    this.accessibilityService.reset$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.resetToDefault());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this._cleanupFeature();
  }

  public nextState(): void {
    this.currentState = (this.currentState + 1) % this.states.length;
    this._runStateLogic();
  }

  private resetToDefault(): void {
    if (this.currentState !== 0) {
      this.currentState = 0;
      this._runStateLogic();
    }
  }

  public getDisplayLabel(): string {
    return this.states[this.currentState];
  }

  public getAriaLabel(): string {
    const nextStateName =
      this.states[(this.currentState + 1) % this.states.length];
    return `Hide Images feature. Current state: ${
      this.states[this.currentState]
    }. Click to change to ${nextStateName}.`;
  }

  private _cleanupFeature(): void {
    this._styleElement?.remove();
    this._styleElement = null;
  }

  private _runStateLogic(): void {
    this._cleanupFeature();

    if (this.currentState === 0) {
      return;
    }

    const css = `
      img:not(#ega-accessibility-widget img),
      picture:not(#ega-accessibility-widget picture),
      svg:not(#ega-accessibility-widget svg),
      [role="img"]:not(#ega-accessibility-widget [role="img"]) {
      display: none !important;
      }
      body *:not(#ega-accessibility-widget *) {
        background-image: none !important;
      }
    `;

    this._styleElement = this.renderer.createElement('style');

    if (this._styleElement) {
      this._styleElement.textContent = css;
      this.renderer.appendChild(this.document.head, this._styleElement);
    }
  }
}
